import React, { useState, useContext, useEffect } from "react";
import Folder from "./Folder";
import { formatMonto } from "../utils";
import { useParams } from "react-router-dom";
import {
  getConceptosKeys,
  getSumConcepto,
  isAirConditioner,
  getEffectiveDefaultType,
  DEFAULT_TYPE_SOLICITADO,
  DEFAULT_TYPE_SUPERVISADO,
  DEFAULT_TYPE_AUTORIZADO,
} from "../utils/conceptos";
import useProyecto from "../hooks/useProyecto";
import PillTabs from "../components/common/PillTabs";
import { ModalContext } from "../context/ModalContext";
import { CapacitorContext } from "../context/CapacitorContext";
import { ConceptosContext } from "../context/ConceptosContext";
import { EstimacionesContext } from "../context/EstimacionesContext";
import BitacoraConcepto from "../components/conceptos/BitacoraConceptos";
import BitacoraService from "../services/BitacoraService";
import ConceptoGenerador from "../components/conceptos/ConceptoGenerador";
import ConceptoMedicionForm from "../components/conceptos/ConceptoMedicionForm";
import { EstimacionVersionesContext } from "../context/EstimacionVersionesContext";
import { ConceptosUbicacionesContext } from "../context/ConceptosUbicacionesContext";
import EstimacionVersionHeader from "../components/estimacionesversiones/EstimacionVersionHeader";
import ConceptoMedicionFormMobile from "../components/conceptos/ConceptoMedicionFormMobile";

const SingleConceptoGenerador = ({ idConcepto }) => {
  const [edited, setEdited] = useState(null);
  const [fileView, setFileView] = useState("reporte");
  const [currentFolder, setCurrentFolder] = useState(null);
  const [comentariosCount, setComentariosCount] = useState(0);
  const { platform } = useContext(CapacitorContext);
  const { concepto, getSingleConcepto } = useContext(ConceptosContext);
  const { selectEstimacion } = useContext(EstimacionesContext);
  const { clearModal, modalComponent } = useContext(ModalContext);
  const { estimacionversion } = useContext(EstimacionVersionesContext);
  const {
    conceptosubicaciones,
    createConceptoUbicacion,
    getConceptosUbicacionConcepto,
  } = useContext(ConceptosUbicacionesContext);

  const keys = getConceptosKeys(concepto || {});

  const { idProyecto, permiso } = useProyecto();
  const { idContrato, idEstimacion, idEstimacionVersion } = useParams();

  const isAC = isAirConditioner(concepto);

  useEffect(() => {
    if (Array.isArray(conceptosubicaciones)) {
      const newConcepto = conceptosubicaciones.find(({ idConceptoUbicacion }) =>
        isNaN(parseInt(idConceptoUbicacion))
      );
      if (newConcepto) setEdited(newConcepto.idConceptoUbicacion);
    }
    if (Array.isArray(conceptosubicaciones) && estimacionversion !== null) {
      handleFolder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conceptosubicaciones, estimacionversion]);

  useEffect(() => {
    fetchConcepto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idConcepto]);

  useEffect(() => {
    if (idConcepto && idEstimacionVersion) {
      BitacoraService.getEntradas(idProyecto, 1000, 0, {
        idConcepto,
        idEstimacionVersion,
      }).then((res) => setComentariosCount(res.data.entradas.length));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idConcepto, idEstimacionVersion]);

  useEffect(() => {
    selectEstimacion(idProyecto, idContrato, idEstimacion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEstimacion]);

  useEffect(() => {
    if (edited !== null && platform !== "web") {
      const conceptoGenerador = conceptosubicaciones.find(
        (concepto) => concepto.idConceptoUbicacion === edited
      );
      modalComponent(
        "Editar Medicion",
        <ConceptoMedicionFormMobile
          idProyecto={idProyecto}
          idContrato={idContrato}
          handleCancel={handleCancelEdit}
          handleCallback={handleSaveCallback}
          idEstimacionVersion={idEstimacionVersion}
          key={conceptoGenerador.idConceptoUbicacion}
          concepto={{ ...conceptoGenerador, ...concepto }}
        />
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edited, platform]);

  const fetchConcepto = () => {
    getSingleConcepto(idProyecto, idConcepto);
    getConceptosUbicacionConcepto(idProyecto, idEstimacionVersion, idConcepto);
  };

  const handleSaveCallback = () => {
    setEdited(null);
    fetchConcepto();
  };

  const handleFolder = () => {
    const conceptoFolder = estimacionversion.folders.find(
      (folder) => folder.idConcepto === idConcepto
    );
    if (conceptoFolder) {
      setCurrentFolder(conceptoFolder);
    }
  };

  const handleComments = (concepto) => {
    modalComponent(
      "Comentarios",
      <BitacoraConcepto
        idFolder={currentFolder.idFolder}
        idConceptoUbicacion={concepto.idConceptoUbicacion}
      />
    );
  };

  const handleCancelEdit = () => {
    getConceptosUbicacionConcepto(idProyecto, idEstimacionVersion, idConcepto);
    setEdited(null);
    clearModal();
  };

  const renderFolder = () => {
    if (currentFolder !== null) {
      const filter =
        fileView === "croquis"
          ? (adjunto) => adjunto.nombre.includes("CROQUIS")
          : (adjunto) => !adjunto.nombre.includes("CROQUIS");
      return (
        <Folder
          size="sm"
          params={{ idConcepto, idEstimacionVersion }}
          idFolder={currentFolder.idFolder}
          hideFolderButton
          filter={filter}
        />
      );
    }
  };

  const getTotalConcepto = (concepto) => {
    let total = 0;
    let totalAutorizado = 0;
    let totalSupervisado = 0;
    (conceptosubicaciones || []).forEach((conceptoUbicacion) => {
      const medicionParams = { unidad: conceptoUbicacion.unidad, clave: conceptoUbicacion.clave };
      const findByEffectiveType = (defaultType) =>
        conceptoUbicacion.ConceptoMedicions.find(
          ({ Usuario }) =>
            getEffectiveDefaultType(Usuario?.defaultType) === defaultType
        );
      const solicitado = findByEffectiveType(DEFAULT_TYPE_SOLICITADO);
      if (solicitado)
        total += getSumConcepto({ ...solicitado, ...medicionParams });
      const supervisado = findByEffectiveType(DEFAULT_TYPE_SUPERVISADO);
      if (supervisado)
        totalSupervisado += getSumConcepto({ ...supervisado, ...medicionParams });
      const autorizado = findByEffectiveType(DEFAULT_TYPE_AUTORIZADO);
      if (autorizado)
        totalAutorizado += getSumConcepto({ ...autorizado, ...medicionParams });
    });
    return [total, totalSupervisado, totalAutorizado];
  };

  const renderTotal = () => {
    if (Array.isArray(conceptosubicaciones) && concepto !== null) {
      const [solicitado, supervisado, autorizado] = getTotalConcepto(concepto);
      return (
        <tr className="border">
          <td colSpan={isAC ? 1 : 4} />
          {[solicitado, supervisado, autorizado].map((total, index) => {

            return (
              <>
                <td
                  colSpan={keys.length}
                  key={`total-label-${index}`}
                  className="border bold text-end"
                >
                  Total
                </td>
                <td key={`total-${index}`} className="border bold">
                  {formatMonto(isNaN(total) ? 0 : total)}
                </td>
              </>
            );
          })}
        </tr>
      );
    }
  };

  const renderGeneradores = () => {
    if (Array.isArray(conceptosubicaciones)) {
      console.log({ conceptosubicaciones });
      return conceptosubicaciones.map((conceptoGenerador) => {
        if (
          edited === conceptoGenerador.idConceptoUbicacion &&
          platform === "web"
        ) {
          return (
            <ConceptoMedicionForm
              idProyecto={idProyecto}
              idContrato={idContrato}
              handleCancel={handleCancelEdit}
              handleCallback={handleSaveCallback}
              idEstimacionVersion={idEstimacionVersion}
              key={conceptoGenerador.idConceptoUbicacion}
              concepto={{ ...conceptoGenerador, ...concepto }}
            />
          );
        }
        return (
          <ConceptoGenerador
            idProyecto={idProyecto}
            idContrato={idContrato}
            handleEdit={setEdited}
            disabled={edited !== null}
            handleComments={handleComments}
            key={conceptoGenerador.idConceptoUbicacion}
            concepto={{ ...conceptoGenerador, ...concepto }}
          />
        );
      });
    }
  };

  const getHeaders = () => {
    const headers = [];
    if (concepto && concepto !== null) {
      if (!isAC) {
        headers.push(
          ...[
            <th key="tramo" className="border">
              Tramo
            </th>,
            <th key="tipo" className="border">
              Tipo
            </th>,
            <th key="nivel" className="border">
              Nivel
            </th>,
          ]
        );
      }
      for (let i = 0; i < 3; i++) {
        if (Array.isArray(keys)) {
          keys.forEach((key) =>
            headers.push(
              <th key={`${key}-${i}`} className="text-uppercase border">
                {key}
              </th>
            )
          );
          headers.push(
            <th key={`total-${i}`} className="text-uppercase border">
              Total
            </th>
          );
        }
      }
    }
    return headers;
  };

  const renderActions = () => {
    if (estimacionversion.estado === "creada" && permiso === "contratista") {
      return (
        <div className="row">
          <div className="container-fluid px-md-0 text-end my-3">
            <button
              disabled={edited !== null}
              className="btn btn-outline-primary me-2"
            >
              <i className="fa fa-upload me-1" />
              Subir Excel
            </button>
            <button
              disabled={edited !== null}
              onClick={createConceptoUbicacion}
              className="btn btn-outline-primary"
            >
              <i className="fa fa-plus me-1" /> Medicion
            </button>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="container-fluid px-0">
      <div className="card shadow my-3 py-3 px-md-4 px-3">
        <EstimacionVersionHeader
          page="3"
          pages="3"
          view="generador"
          title="HOJA GENERADORA DE VOLUMEN DE OBRA"
        />
        {renderActions()}
        <div className="row">
          <div className="col-12 col-xl-6 px-md-0 px-1">
            <div className="border bg-light">
              <h3 className="h6 bold w-100 mt-2 text-center">
                REPORTE FOTOGRAFICO
              </h3>
              <div className="container-fluid py-2 px-4">
                <PillTabs
                  value={fileView}
                  modifier={setFileView}
                  tabs={[
                    {
                      label: "ARCHIVOS / FOTOS",
                      value: "reporte",
                    },
                    {
                      label: "CROQUIS",
                      value: "croquis",
                    },
                  ]}
                />
              </div>
            </div>
            <div className="container-fluid border p-2">{renderFolder()}</div>
          </div>
          <div className="col-12 col-xl-6 px-md-0">
            <div className="text-end mb-2">
              <button
                className={`btn btn-sm btn-outline-${comentariosCount > 0 ? "primary" : "secondary"}`}
                onClick={() =>
                  modalComponent(
                    `Comentarios — ${concepto?.clave}`,
                    <BitacoraConcepto
                      idFolder={currentFolder?.idFolder}
                      idConcepto={concepto?.idConcepto}
                      idEstimacionVersion={idEstimacionVersion}
                    />
                  )
                }
              >
                <i className="fa fa-comments" />
                {comentariosCount > 0 && (
                  <span className="ms-1">{comentariosCount}</span>
                )}
              </button>
            </div>
            <div className="table-responsive">
              <table className="table">
                <thead style={{ height: 85 }}>
                  <tr className="bg-light border">
                    <th colSpan={isAC ? 1 : 4} className="text-center border">
                      UBICACIÓN
                    </th>
                    <th
                      colSpan={isAC ? 5 : keys.length + 1}
                      className="text-center border"
                    >
                      CONTRATISTA
                    </th>
                    <th
                      colSpan={isAC ? 5 : keys.length + 1}
                      className="text-center border"
                    >
                      SUPERVISOR
                    </th>
                    <th
                      colSpan={isAC ? 5 : keys.length + 1}
                      className="text-center border"
                    >
                      OBRAS
                    </th>
                    <th className="text-center border" />
                  </tr>
                  <tr className="bg-light border position-relative text-uppercase">
                    {platform !== "web" && <th className="border">Menú</th>}
                    <th className="border">Eje</th>
                    {getHeaders()}
                    {platform === "web" && <th className="border">Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {renderGeneradores()}
                  {renderTotal()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleConceptoGenerador;
